import { useEffect } from 'react'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import Stats from '@/widgets/CustomerStats'

import useCustomerData from '@/hooks/useCustomerData'
import { useLeadTherapistFilter } from './filters/LeadTherapistFilter'
import { useDateRangeFilter } from './filters/DateRangeFilter'

// Custom hooks
import { useCustomerSelection } from './hooks/useCustomerSelection'
import { useCustomerActions } from './hooks/useCustomerActions'
import { useCustomerSearch } from './hooks/useCustomerSearch'

// Components
import SearchActionsBar from '@/widgets/SearchActionsBar'
import CustomerTable from './components/CustomerTable'

import LoadMoreSection from '@/widgets/LoadMoreSection'

export default function Customers() {
	const { t } = useTranslation()

	const {
		customerData,
		loadingCustomers: loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadCustomerData,
		handleLoadMore,
		setError,
		setCustomerSorting,
		updateFilters,
		sortColumn,
		sortDirection,
		filters,
	} = useCustomerData()

	// Custom hooks
	const { selectedCustomers, handleSelectAll, handleSelectCustomer, clearSelection, getSelectionState } =
		useCustomerSelection()
	const { isDeleting, handleDeleteSelected } = useCustomerActions(loadCustomerData)
	const { searchTerm, handleSearchChange } = useCustomerSearch(updateFilters)

	// Filter hooks
	const { selectedTherapistId, handleLoadTherapist } = useLeadTherapistFilter(updateFilters)
	const { fromDate, toDate, handleDateRangeFilter } = useDateRangeFilter(updateFilters)

	// Handle errors with toast notifications
	useEffect(() => {
		if (error) {
			Swal.fire({
				title: i18n.t('notifications.errorTitle'),
				text: error,
				icon: 'error',
			}).then(result => {
				setError()
			})
		}
	}, [error, setError])

	useEffect(() => {
		loadCustomerData()
	}, [loadCustomerData])

	// Handle column sorting
	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setCustomerSorting(column, newDirection)
	}

	// Get selection state for the table
	const { isAllSelected, isIndeterminate } = getSelectionState(customerData)

	// Wrapper for delete action
	const onDeleteSelected = () => {
		handleDeleteSelected(selectedCustomers, clearSelection)
	}

	// Wrapper for select all
	const onSelectAll = checked => {
		handleSelectAll(checked, customerData)
	}

	const buttons = [
		{
			type: 'delete',
			count: selectedCustomers.size,
			onClick: onDeleteSelected,
			loading: isDeleting,
			label: t('customers.list.actions.delete'),
		},
		{
			type: 'date',
			active: !!(fromDate || toDate),
			onClick: handleDateRangeFilter,
			label: t('customers.list.actions.dateCreated'),
		},
		{ type: 'add', to: '/system/customers/add', label: t('customers.list.actions.addNew') },
	]

	return (
		<>
			<div className='hidden md:block mb-2'>
			<Stats />
			</div>

			<SearchActionsBar
				value={searchTerm}
				onChange={handleSearchChange}
				placeholder={t('customers.list.searchPlaceholder')}
				buttons={buttons}
			/>

			<CustomerTable
				customerData={customerData}
				loading={loading}
				selectedCustomers={selectedCustomers}
				onSelectAll={onSelectAll}
				onSelectCustomer={handleSelectCustomer}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={customerData.length}
				totalCount={totalCount}
			/>
		</>
	)
}
